import React from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Icons } from "components";
import { CelendarWidgetStyled } from "./styled";
import moment from "moment";

const CelendarWidget = ({
  theme_bosendorfer,
  startDate,
  timeSlotData,
  holidayData,
  type,
  timeData = TIME_DATA,
  // วันในสัปดาห์ที่ปิดประจำ (0=อาทิตย์ 1=จันทร์ ...) เช่น ห้องซ้อมปิดวันจันทร์ แสดงทั้งคอลัมน์เป็น "วันหยุด"
  closedWeekdays = [],
  onClickPrevWeek,
  onClickNextWeek,
}) => {
  const customClass = cx({
    theme_bosendorfer: theme_bosendorfer,
  });

  const { i18n } = useTranslation([""]);

  moment.locale(theme_bosendorfer ? "en" : i18n.language);

  const _getStatusColor = (status) => {
    if (theme_bosendorfer) {
      switch (status) {
        case "1":
          return "#FFFFFF";
        case "2":
          return "#96969669";
        case "3":
        case "4":
          return "#00000069";
        default:
          return "#A694B8";
      }
    } else {
      switch (status) {
        case "1":
          return "#4B1E7869";
        case "2":
          return "#B4A1C766";
        case "3":
        case "4":
          return "#FF0F0069";
        default:
          return "#A694B8";
      }
    }
  };

  // ห้องซ้อม: ใบจองที่แอดมินอนุมัติแล้ว (3 รอสลิป, 4 รอตรวจสลิป, 5 สมบูรณ์) ถือว่าไม่ว่าง
  // ที่เหลือ (ร่าง/รอแอดมินยืนยัน/ขอข้อมูลเพิ่ม) ยังเป็นคิวรอยืนยัน
  // วันหยุด/ปิดห้อง/ซ่อมบำรุงไม่มี booking_status จึงเป็นไม่ว่าง
  const _getRoomSlotStatus = (slots) => {
    if (slots.length === 0) return "available";
    const isUnavailable = slots.some(
      (f) =>
        f.reason_closed !== "4" || ["3", "4", "5"].includes(f.booking_status),
    );
    return isUnavailable ? "full" : "pending";
  };

  const _renderRoomStatusIcon = (status) => {
    switch (status) {
      case "full":
        return <Icons.StatusCross width="12" height="12" />;
      case "pending":
        return <Icons.StatusDot width="12" height="12" />;
      default:
        return null;
    }
  };

  const _renderDay = () => {
    const startOfWeek = startDate
      ? moment(startDate, "YYYY-MM-DD").startOf("week")
      : moment().startOf("week");
    let days = [];
    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={`day_${i}`} className="day_cell">
          <div>{startOfWeek.format("ddd")}</div>
          <div>{startOfWeek.format("D")}</div>
        </div>,
      );
      startOfWeek.add(1, "day");
    }
    return days;
  };

  const _renderDataCells = (e, rowIndex) => {
    let days = [];
    const holidayDates = holidayData.map((f) =>
      moment(f.date).format("YYYY-MM-DD"),
    );
    const startOfWeek = startDate
      ? moment(startDate, "YYYY-MM-DD").startOf("week")
      : moment().startOf("week");
    for (let i = 0; i < 7; i++) {
      const key = `cell_${rowIndex}_${i}`;
      // วันปิดประจำสัปดาห์: ใช้ช่องเดียวยาวทั้งคอลัมน์ (span ทุกแถวเวลา) วางแค่แถวแรก
      // แถวถัดไปไม่ต้องวาง grid จะข้ามช่องที่ถูกจองไว้เอง คอลัมน์วันอื่นยังตรงเหมือนเดิม
      if (closedWeekdays.includes(startOfWeek.day())) {
        if (rowIndex === 0) {
          days.push(
            <div
              key={key}
              className="data_cell closed_day"
              style={{ gridRow: `span ${timeData.length - 1}` }}
            >
              {i18n.language === "en" ? "Closed" : "วันหยุด"}
            </div>,
          );
        }
      } else if (holidayDates.includes(startOfWeek.format("YYYY-MM-DD"))) {
        // HOLIDAY
        days.push(
          <div
            key={key}
            className={`data_cell ${type === "room" ? "full" : ""}`}
            style={type === "room" ? {} : { background: _getStatusColor("3") }}
          >
            {type === "room" && _renderRoomStatusIcon("full")}
          </div>,
        );
      } else {
        // Check if any booking overlaps with this time slot
        // Overlap occurs when: booking starts before slot ends AND booking ends after slot starts
        const slotStart = parseFloat(e.value.startTime);
        const slotEnd = parseFloat(e.value.endTime);
        const overlapSlots = timeSlotData.filter(
          (f) =>
            moment(f.date).format("YYYY-MM-DD") ===
              startOfWeek.format("YYYY-MM-DD") &&
            parseFloat(f.start_time) < slotEnd &&
            parseFloat(f.end_time) > slotStart,
        );

        const roomStatus =
          type === "room" ? _getRoomSlotStatus(overlapSlots) : null;
        days.push(
          type === "room" ? (
            <div key={key} className={`data_cell ${roomStatus}`}>
              {_renderRoomStatusIcon(roomStatus)}
            </div>
          ) : (
            <div
              key={key}
              className="data_cell"
              style={{
                background: _getStatusColor(overlapSlots[0]?.status || "1"),
              }}
            />
          ),
        );
      }
      startOfWeek.add(1, "day");
    }
    return days;
  };

  const _renderLegend = () => {
    const isEn = theme_bosendorfer || i18n.language === "en";
    const legend =
      type === "room"
        ? [
            { key: "available", label: isEn ? "Available" : "ว่าง" },
            { key: "pending", label: isEn ? "Pending" : "รอยืนยัน" },
            { key: "full", label: isEn ? "Unavailable" : "ไม่ว่าง" },
            ...(closedWeekdays.length > 0
              ? [{ key: "closed_day", label: isEn ? "Closed" : "วันหยุด" }]
              : []),
          ]
        : [
            { key: "full", label: isEn ? "Unavailable" : "ไม่ว่าง" },
            {
              key: "almost",
              label: theme_bosendorfer
                ? "Almost Full"
                : isEn
                  ? "Almost full"
                  : "ใกล้เต็ม",
            },
            { key: "available", label: isEn ? "Available" : "ว่าง" },
          ];
    return (
      <div className={`status_legend ${type === "room" ? "room" : ""}`}>
        {legend.map((e) => (
          <div key={e.key} className="status_detail">
            <div className={`box ${e.key}`}>
              {type === "room" && _renderRoomStatusIcon(e.key)}
            </div>
            <div className="status_title">{e.label}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <CelendarWidgetStyled className={customClass}>
      <div className="cd_header_row">
        <div className="top_left">
          {type === "room" ? "" : moment().format("MMMM YYYY")}
        </div>
        <div className="action_next_prev_block">
          <div className="top_right" onClick={onClickPrevWeek}>
            {theme_bosendorfer
              ? "< Back"
              : i18n.language === "en"
                ? "< Previous"
                : `< ย้อนกลับ`}
          </div>
          <div className="top_right" onClick={onClickNextWeek}>
            {theme_bosendorfer
              ? "Next Week >"
              : i18n.language === "en"
                ? "Next >"
                : `อาทิตย์ถัดไป >`}
          </div>
        </div>
      </div>
      {_renderLegend()}
      {/* ใช้ grid เดียวทั้งตาราง แถวเวลากับคอลัมน์วันจะตรงกันเสมอ */}
      <div className={`cd_body ${type === "room" ? "room" : ""}`}>
        <div className="time_cell f_16">{timeData[0]?.label}</div>
        {_renderDay()}
        {timeData.slice(1).map((e, rowIndex) => (
          <React.Fragment key={`row_${rowIndex}`}>
            <div className="time_cell">{e.label}</div>
            {_renderDataCells(e, rowIndex)}
          </React.Fragment>
        ))}
      </div>
    </CelendarWidgetStyled>
  );
};

const TIME_DATA = [
  { label: "Time" },
  { label: "10.00\n11.00", value: { startTime: "10.00", endTime: "11.00" } },
  { label: "11.00\n12.00", value: { startTime: "11.00", endTime: "12.00" } },
  { label: "13.00\n14.00", value: { startTime: "13.00", endTime: "14.00" } },
  { label: "14.00\n15.00", value: { startTime: "14.00", endTime: "15.00" } },
  { label: "15.00\n16.00", value: { startTime: "15.00", endTime: "16.00" } },
  { label: "16.00\n17.00", value: { startTime: "16.00", endTime: "17.00" } },
  { label: "17.00\n18.00", value: { startTime: "17.00", endTime: "18.00" } },
];

export default CelendarWidget;
